/**
 * AccessibilityDrawer — Shared drawer component for all SPAs.
 *
 * Renders a trigger IconButton, a right-anchored MUI Drawer containing
 * AccessibilityPanel, the CVDFilterOverlay (hidden SVG defs), and global
 * effects for CVD filter application and focus-visible indicators.
 *
 * @module components/accessibility/AccessibilityDrawer
 */
import React, { useState, useEffect } from 'react';
import { Drawer, IconButton, Box } from '@mui/material';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import { AccessibilityPanel } from './AccessibilityPanel';
import { CVDFilterOverlay, CVD_FILTER_IDS } from './CVDFilterOverlay';
import { useAccessibilitySettings, type CvdMode } from '../../hooks/useAccessibilitySettings';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DRAWER_WIDTH = 420;
const FOCUS_VISIBLE_STYLE_ID = 'verisa-a11y-focus-visible';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AccessibilityDrawer(): React.ReactElement {
  const { _: lingui } = useLingui();
  const { cvdMode } = useAccessibilitySettings();
  const [open, setOpen] = useState(false);

  // Apply CVD filter to documentElement
  useEffect(() => {
    const filterValue = CVD_FILTER_IDS[cvdMode as CvdMode] ?? '';
    if (filterValue) {
      document.documentElement.style.filter = filterValue;
    } else {
      document.documentElement.style.removeProperty('filter');
    }
    return () => {
      document.documentElement.style.removeProperty('filter');
    };
  }, [cvdMode]);

  // Inject global focus-visible styles
  useEffect(() => {
    if (document.getElementById(FOCUS_VISIBLE_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = FOCUS_VISIBLE_STYLE_ID;
    style.textContent = `*:focus-visible { outline: 2px solid #1565C0; outline-offset: 2px; }`;
    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(FOCUS_VISIBLE_STYLE_ID);
      if (el) el.remove();
    };
  }, []);

  return (
    <>
      <CVDFilterOverlay />

      <IconButton
        color="inherit"
        onClick={() => setOpen(true)}
        aria-label={lingui(msg`Accessibility settings`)}
        data-testid="a11y-drawer-trigger"
      >
        <AccessibilityNewIcon />
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: {
              width: { xs: '100%', sm: DRAWER_WIDTH },
            },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <AccessibilityPanel />
        </Box>
      </Drawer>
    </>
  );
}

export default AccessibilityDrawer;
